import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, DefaultTheme } from 'styled-components';
import { format } from 'date-fns';
import * as Sentry from '@sentry/react';
// Trazado de tramos SIN RED: recorta la malla que la app ya
// dejó cacheada para resolver el PK offline.
import { segmentPathFromMesh } from '../../../../../utils/segmentPathFromMesh';
import { orderStretchPaths } from '../../../../../utils/stretchPathOrder';
import { getCachedRoadMesh } from '../../../../../hooks/usePrecacheRouteMesh';

// MultiPathMap reutiliza los primitives internos del MapComponent canónico
// (ver src/components/display/Map/_internal.tsx) sin alterar su API pública.
import { MultiPathMap } from '../../../../../components/display/Map/MultiPathMap';
import type { MultiPathEntry, MultiPathMarker } from '../../../../../components/display/Map/MultiPathMap';

// GQL — usamos client.query con fetchPolicy 'cache-first' para que Apollo
// deduplique por variables entre segmentos con PKs idénticos.
import { client } from '../../../../../gql/client';
import { GET_POSITION_ROADS } from '../../../../../gql/queries/geoPositions/geoPositionRoads';

// Icons — el toggle colapsado usa el icono estándar de capas/leyenda
// (mismo símbolo que Google Maps/Leaflet para "layers"), vendorizado desde
// Lucide (`lucide-static`, licencia ISC) como LayersIcon.svg. Reutiliza el
// mismo `aria-label` `common:expandInfo` que el toggle "desplegar info" de
// RouteHeaderCard (patrón canónico ya existente en este módulo), sin i18n
// key nueva. Cierre = CloseIcon, usado igual en HamburguerMenu (decorativo,
// el botón que lo envuelve lleva el aria-label).
// SVG inline (componentes, no `<img>`): heredan el color PRIMARY del tema vía
// `stroke="currentColor"` — un `<img>` no hereda color y la máscara CSS falla
// con las URLs de assets de Vite (pintaba el fondo, no el trazo).
import { LayersIcon } from '../../../../../assets/icons/LayersIcon';
// Botón de recentrar (2026-07-08): icono estándar "locate" (círculo +
// ticks, el glifo de recentrar/mi-posición de las apps de mapas),
// vendorizado desde Lucide (`lucide-static` v1.23.0, licencia ISC).
import { LocateIcon } from '../../../../../assets/icons/LocateIcon';
// Pantalla completa (2026-09-01): par maximize/minimize de Lucide (ISC), el
// glifo de las cuatro esquinas que usan reproductores y mapas.
import { MaximizeIcon } from '../../../../../assets/icons/MaximizeIcon';
import { MinimizeIcon } from '../../../../../assets/icons/MinimizeIcon';
import { CloseIcon } from '../../../../../assets/icons/CloseIcon';
// Fusión de marcadores (2026-07-09): variante JSX del mismo glifo ↺ que se
// dibuja DENTRO del marcador fusionado del mapa (ver
// `directionChangeIcon`/`createDirectionChangeMarkerContent` más abajo) —
// usada aquí solo en la leyenda (DOM plano, no el canvas de Google Maps).
import { DirectionChangeIcon } from '../../../../../assets/icons/DirectionChangeIcon';

// Pantalla completa: Escape, «atrás» de Android y bloqueo del scroll mientras
// el mapa está expandido (ver el JSDoc del hook).
import { useFullscreenToggle } from '../../../../../hooks/useFullscreenToggle';

// Types
import {
  RouteSegment,
  RouteGpsPoint,
} from '../../../../../types/surveillanceRoute';
// GPS en vivo (2026-07-23, plan `rv-mapa-gps-tiempo-real` Task A3) — mismos
// tipos que produce `useSurveillanceRouteGpsTracking` (Task A2), para que el
// cursor/traza que pinta este mapa nunca diverjan de lo que el hook expone.
import type {
  LivePosition,
  LiveTrackPoint,
} from '../../../../../hooks/useSurveillanceRouteGpsTracking';

// Utils — normaliza el vocabulario `direction` (top/bottom/both vs.
// ascending/descending legacy) antes de comparar "¿cambió de sentido?".
// Mismo helper que usa SegmentList/RouteSegmentOT/PDF — sin esto, dos
// tramos con el MISMO sentido semántico pero vocabulario distinto
// (`top` vs `ascending`) se leerían como un cambio de sentido falso
// (la clase de bug que este helper existe para evitar, ver su comentario).
import {
  directionNameKey,
  normalizeDirectionForQuery,
} from '../../../../../utils/surveillanceRoute';
// Agrupación de marcadores por distancia EN PANTALLA (radio 60 px, el de la
// librería oficial de Google). Vive en un util propio para poder probarla
// aislada: la E2E corre sin la API de Maps, así que nunca llega a emitir un
// cambio de zoom con el que ejercitarla.
import {
  clusterByScreenDistance,
  spiderfyPositions,
} from '../../../../../utils/mapClustering';

// Styles
import {
  MapContainer,
  MapWrapper,
  LegendContainer,
  LegendItem,
  LegendColor,
  LegendLabel,
  LegendDirectionChangeGlyph,
  LegendToggleButton,
  LegendToggleIcon,
  LegendHeader,
  LegendCloseButton,
  LegendSectionTitle,
  RecenterButton,
  FullscreenButton,
  NoMapMessage,
} from './styles';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface MapWithSegmentsProps {
  segments: RouteSegment[];
  gpsPoints?: RouteGpsPoint[];
  /** Carretera PRINCIPAL: la que encuadra el mapa y dibuja el trazado gris. */
  roadId?: string;
  /**
   * Todas las carreteras del recorrido (multi-carretera 2026-08-15). Cada tramo
   * se traza sobre la malla de LA SUYA — dibujarlo sobre la de la troncal
   * cuando va por el ramal lo pintaría a kilómetros de donde estuvo.
   */
  roadIds?: string[];
  /**
   * Qué trozo de CADA carretera dibujar de fondo. Sin esto solo se pintaba la
   * primera, así que un recorrido de troncal + ramal enseñaba media verdad.
   *
   * Hace falta una ventana por carretera y no un rango único porque sus
   * kilometrajes no son comparables: el 234+000 de la C-25 y el de su ramal son
   * sitios distintos, así que filtrar el ramal por el rango de la troncal
   * devolvería un trozo arbitrario.
   *
   * Ausente = comportamiento de siempre: una sola carretera con el rango suelto.
   */
  roadWindows?: Array<{
    roadId: string;
    pkInitKm: number;
    pkInitMeter: number;
    pkEndKm: number;
    pkEndMeter: number;
  }>;
  pkInitKm?: number;
  pkInitMeter?: number;
  pkEndKm?: number;
  pkEndMeter?: number;
  direction?: string;
  onSegmentClick?: (segment: RouteSegment) => void;
  /**
   * Id del tram seleccionado (tabla→mapa, 2026-07-08) — resalta su
   * polyline (más gruesa + casing oscura + flechas de sentido) y atenúa el
   * resto. `undefined`/vacío = sin selección, comportamiento previo
   * byte-idéntico. Ver `paths` useMemo.
   */
  selectedSegmentId?: string;
  /** Wizard Nuevo recorrido (2026-07-04): pinta solo la línea del recorrido,
   *  sin la leyenda de estados (Completado/Pausado/En progreso) — como el
   *  mapa del wizard de actuación. */
  hideLegend?: boolean;
  /**
   * GPS en vivo (Task A2/A3, 2026-07-23) — posición actual del tracker,
   * pintada como cursor SIN esperar al backend (flush cada 15s / refetch).
   * `undefined`/`null` = sin lectura viva todavía → fallback al último
   * punto de `gpsPoints` (comportamiento previo, roles de solo-lectura o
   * antes del primer fix GPS).
   */
  livePosition?: LivePosition | null;
  /** Traza en vivo del tramo activo actual (mismo array que expone el hook). */
  liveTrack?: LiveTrackPoint[];
  /**
   * `startDate` del tramo ACTIVO (el único con `endDate` nulo). Acota la
   * línea azul al tramo en curso — sin tramo activo (ruta pausada/cerrada)
   * NO hay línea azul, solo los tramos cerrados en verde vía PKs.
   */
  activeSegmentStartDate?: string | null;
  /**
   * Pantalla completa (2026-09-01) — muestra el botón que expande el mapa a
   * todo el viewport. Apagado por defecto: hoy solo lo enciende el detalle
   * del recorrido (`RoutePreviewPage`), que es donde se LEE el recorrido y
   * donde el alto fijo de 40rem se queda corto. En la pestaña Ubicació del
   * asistente el operario está rellenando un formulario, así que taparle la
   * pantalla con el mapa estorbaría más que ayudar.
   */
  allowFullscreen?: boolean;
}

interface Coordinates {
  lat: number;
  lng: number;
}

type SegmentStatus = 'completed' | 'active';

interface SegmentPath {
  segmentId: string;
  status: SegmentStatus;
  coordinates: Coordinates[];
}

/**
 * Punto que un tram cerrado deja en el mapa, ANTES de agruparlo por
 * coordenada. Varios tramos de un mismo recorrido acaban en el mismo PK (la
 * patrulla va y vuelve), así que esto no es todavía un marcador — ver
 * `dotMarkers`.
 */
interface SegmentDot {
  segment: RouteSegment;
  position: Coordinates;
  /** Color de la causa de pausa (o negro si cierra sin causa pero gira). */
  color: string;
  /** Su punto final es un cambio de sentido. */
  hasGlyph: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════

// UX 2026-07-07 (feedback usuario, datos reales): el workflow SIEMPRE cierra
// un tram declarando un `pauseCause` (intervención leve/aviso a sala/actuación)
// — es la propia mecánica de cierre, no una señal de que el tram "quedó
// pausado". Con el mapeo antiguo (cerrado+causa=naranja "Pausado") una ruta
// entera, incluso validada/finalizada, se pintaba naranja para siempre y
// "Completado (0)" era ruido permanente. Semántica correcta = ciclo de vida
// de la RUTA sobre el mapa: cualquier tram CERRADO ya fue patrullado
// (verde), el tram ACTIVO es el único en curso (azul). El detalle de la
// causa de pausa sigue vivo en la tabla de Tramos (`SegmentList` — columna
// "Causa"), no en el color de la línea del mapa.
const getSegmentStatus = (segment: RouteSegment): SegmentStatus =>
  segment.endDate ? 'completed' : 'active';

// Bug 2026-07-09 (trazado planeado pintado como "completado" en verde): id
// sentinela del path de fallback "recorrido completo, sin desglose por
// tramo" (aún sin tramos, o ninguno con PKs cerrados — ver efecto
// `loadAll`). Ese trazado es solo el ENCUADRE de la carretera, nada
// patrullado todavía — pintarlo con `routeStatus.completed` (verde), que es
// lo que hacía `status: 'completed'` sin este fix, mentía "ya patrullado".
// Constante compartida por productor (`loadAll`) y consumidor (`paths`
// useMemo, color neutro) para que ambos usen el MISMO literal.
const ROUTE_FALLBACK_SEGMENT_ID = 'route-fallback';

/**
 * ¿Es este id el del trazado de FONDO del recorrido (y no un tramo)?
 *
 * Con varias carreteras hay una polilínea de fondo por cada una y el id lleva
 * sufijo (`route-fallback:<roadId>`) para que React las distinga. Los dos
 * sitios que tratan el fondo distinto —el gris neutro y el filtro que lo oculta
 * mientras hay tramo activo— tienen que seguir reconociéndolas todas, así que
 * la comprobación vive en UNA función en vez de repetir el `===` con el prefijo.
 */
const isRouteFallbackPath = (segmentId: string): boolean =>
  segmentId === ROUTE_FALLBACK_SEGMENT_ID ||
  segmentId.startsWith(`${ROUTE_FALLBACK_SEGMENT_ID}:`);

/**
 * Rumbo inicial (grados, 0 = norte, sentido horario) entre 2 puntos —
 * fórmula estándar de bearing gran-círculo. Usado para orientar el cursor
 * GPS según el sentido de la marcha (últimos 2 puntos de la traza).
 */
const computeBearing = (from: Coordinates, to: Coordinates): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;
  const phi1 = toRad(from.lat);
  const phi2 = toRad(to.lat);
  const deltaLambda = toRad(to.lng - from.lng);
  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

// V4 D7 — mismos colores semánticos que `PauseCauseChip`
// (src/components/display/ShowOT/RouteSegmentOT/styles.ts): el marcador de
// pausa del mapa reutiliza EXACTAMENTE esos tokens para leerse como la misma
// familia visual que el chip "Causa: X" de la card del tramo.
//   - action → red
//   - external-call → gold (feedback usuario 2026-07-08: con
//     `secondaryOrange` los dots rojo/naranja se confundían de un vistazo en
//     el mapa. `gold` ya existe en ambos temas — rois `#FFD97D` vs
//     `red` `#EE6055`, cora `#FFA02F` vs `red` `#C8372D` — separación clara
//     de hue sin inventar hex nuevo. En el tema cora `gold`===`secondaryOrange`
//     (mismo hex), así que ahí el cambio es un no-op inocuo)
//   - basic (default) → primary
const getPauseCauseColor = (
  cause: string | null | undefined,
  theme: DefaultTheme
): string => {
  switch (cause) {
    case 'action':
      return theme.colors.red;
    case 'external-call':
      return theme.colors.gold;
    case 'basic':
    default:
      return theme.colors.primary;
  }
};

// Orden estable de la leyenda de causas (no depende del orden de llegada de
// los tramos).
const PAUSE_CAUSE_ORDER = ['basic', 'action', 'external-call'];

// Retirado 2026-08-10: `spreadOverlappingMarkers` repartía los marcadores que
// caían en la misma coordenada "en corona", con un radio FIJO en grados
// (0,00003 ≈ 3,3 m). A la escala a la que se mira un recorrido (~55 m/píxel)
// eso es 0,06 píxeles — no separaba NADA en la práctica, solo habría hecho
// algo con el zoom a nivel de calle, y mientras tanto el mapa enseñaba menos
// paradas de las que hubo. Lo sustituye la agrupación por coordenada de
// `dotMarkers` (un dot por punto, ambigüedad resuelta por selección).

// Tabla→mapa selección (2026-07-08): peso/opacidad de las polylines de
// tramo según haya (o no) un tram seleccionado. Ver `paths` useMemo.
const SEGMENT_STROKE_WEIGHT = 7;
const SEGMENT_STROKE_WEIGHT_SELECTED = SEGMENT_STROKE_WEIGHT + 3;
const DIMMED_STROKE_OPACITY = 0.45;
/**
 * Tramo que todavía espera en la cola offline: se pinta más flojo. Que se vea
 * el trabajo, pero que no aparente estar confirmado — mismo criterio que la
 * fila con ⏳ en la tabla.
 */
const PENDING_STROKE_OPACITY = 0.55;

// Zoom con el que arranca el mapa (`MultiPathMap zoom={14}`) — se usa como
// semilla del umbral de agrupación hasta que Google Maps entrega el primero
// real por `idle`.
const DEFAULT_MAP_ZOOM = 14;

// Botón de recentrar (2026-07-08): padding del fitBounds manual — algo más
// generoso que el del encuadre inicial (40px, `_internal.tsx`) para que
// "se vea todo el tramo" con aire alrededor.
const RECENTER_FIT_PADDING_PX = 60;

// Symbol `path` genérico (círculo) — solo sirve de type-guard `'path' in
// icon` en MultiPathMap para distinguir Symbol de Icon; el contenido visual
// real de los marcadores de pausa/cambio de sentido lo deciden
// `createMarkerContentFromSymbol`/`createDirectionChangeMarkerContent` en
// `_internal.tsx` a partir de `fillColor`/`strokeColor`/`strokeWeight`.
const DOT_SYMBOL_PATH = 'M-10,0 A10,10 0 1 1 10,0 A10,10 0 0 1 -10,0';

const hasCompletePks = (segment: RouteSegment): boolean =>
  segment.pkInitKm !== undefined &&
  segment.pkInitMeter !== undefined &&
  segment.pkEndKm !== undefined &&
  segment.pkEndKm !== null &&
  segment.pkEndMeter !== undefined &&
  segment.pkEndMeter !== null;

// Bug 2026-07-08 (sentido de flechas al revés en tramos descendentes):
// `pkInitKm/pkInitMeter` y `pkEndKm/pkEndMeter` son CRONOLÓGICOS — "dónde
// estaba la patrulla al abrir el timer" / "al cerrarlo" (ver
// `useRouteTimerActions.createSegment`/`finishSegment`), SIN ninguna
// normalización min/max. Un tramo DESCENDENTE (PK alto → PK bajo) tiene
// `pkInit` numéricamente MAYOR que `pkEnd`. `GET_POSITION_ROADS`
// (`geoPositionRoad.service.ts` `findAll`), en cambio, SIEMPRE ordena sus
// resultados de PK más bajo a PK más alto, sea cual sea el sentido pedido
// (compara min/max de los 2 extremos antes de filtrar). Sin corregir, las
// coordenadas de un tramo descendente quedarían en el orden FÍSICO
// (bajo→alto), el REVÉS de cómo lo patrulló realmente la ruta — y las
// `directionArrows` (que se auto-rotan al orden de los puntos de la
// polyline) apuntarían al revés.

/**
 * Carga las coordenadas de un segmento disparando GET_POSITION_ROADS.
 * Apollo deduplica por variables con fetchPolicy 'cache-first', por lo que
 * múltiples segmentos con los mismos PKs solo cuestan una query real.
 *
 * Bug 2026-07-08 (Su.0000254, tramo 1 sin dot de pausa): el filtro
 * `geoPositionRoads(direction: ...)` es una IGUALDAD EXACTA contra
 * `GeoPositionRoad.direction`, que en BD SOLO almacena `top`/`down`. Un
 * tramo legacy con `segment.direction === 'ascending'` se pasaba crudo →
 * 0 resultados → el tramo se quedaba sin coordenadas → sin marcador de
 * pausa, aunque `pauseCausesPresent` (basado solo en datos de segmento)
 * SÍ lo listara en la leyenda. `normalizeDirectionForQuery` traduce el
 * vocabulario legacy al canónico antes de consultar (mismo helper que ya
 * usa `directionNameKey` para las comparaciones de cambio de sentido).
 */
/**
 * Trazado del tramo SIN RED, recortado de la malla que ya está cacheada. `[]`
 * si no hay malla — el llamante lo trata como "no hay nada que pintar".
 */
const meshFallback = (
  segment: RouteSegment,
  roadId: string
): Coordinates[] => {
  // Multi-carretera (2026-08-17): un tramo puede haber pasado por varias vías.
  // Se dibuja concatenando sus trozos, cada uno recortado de SU malla — pintarlo
  // entero sobre la malla de la primera lo estiraría por un trazado que la
  // patrulla no recorrió.
  const stretches = segment.roadStretches;
  if (stretches && stretches.length > 0) {
    return stretches.flatMap((stretch) =>
      segmentPathFromMesh(
        {
          ...segment,
          pkInitKm: stretch.pkInitKm,
          pkInitMeter: stretch.pkInitMeter,
          pkEndKm: stretch.pkEndKm,
          pkEndMeter: stretch.pkEndMeter,
        },
        getCachedRoadMesh(stretch.roadId)
      )
    );
  }
  return segmentPathFromMesh(segment, getCachedRoadMesh(roadId));
};

const loadSegmentCoordinates = async (
  segment: RouteSegment,
  roadId: string,
  fallbackDirection: string
): Promise<Coordinates[]> => {
  if (!hasCompletePks(segment)) return [];
  try {
    const rawDirection =
      segment.direction === 'both' || !segment.direction
        ? fallbackDirection === 'both'
          ? 'top'
          : fallbackDirection
        : segment.direction;
    const direction = normalizeDirectionForQuery(rawDirection);

    // 🔴 Multi-carretera (2026-08-19): UNA consulta POR TROZO.
    //
    // Antes se pedía el trazado entero con los PK del tramo y su carretera
    // ESCALAR, que es la de salida. Con un tramo que cruza al ramal eso pide
    // 82+800 → 237+100 sobre la C-25, que acaba en 236+000: el servidor
    // devuelve hasta ahí y el ramal NO SE DIBUJA NUNCA estando online.
    //
    // Los trozos ya se respetaban en `meshFallback`, pero ese es el camino sin
    // cobertura — o sea que lo que se veía a diario era lo que NO tenía en
    // cuenta las vías. De ahí que el tramo apareciera cortado justo donde la
    // patrulla se metía en el ramal.
    const rangos = segment.roadStretches?.length
      ? segment.roadStretches.map((st) => ({
          roadId: st.roadId,
          pkInitKm: st.pkInitKm,
          pkInitMeter: st.pkInitMeter,
          pkEndKm: st.pkEndKm,
          pkEndMeter: st.pkEndMeter,
        }))
      : [
          {
            roadId,
            pkInitKm: segment.pkInitKm as number,
            pkInitMeter: segment.pkInitMeter as number,
            pkEndKm: segment.pkEndKm as number,
            pkEndMeter: segment.pkEndMeter as number,
          },
        ];

    const partes = await Promise.all(
      rangos.map((rango) =>
        client.query({
          query: GET_POSITION_ROADS,
          fetchPolicy: 'cache-first',
          variables: { input: { ...rango, direction } },
        })
      )
    );

    // Si CUALQUIER trozo se queda sin trazado, el dibujo saldría mutilado y
    // sin avisar. Mejor caer al respaldo, que sabe recortar de la malla local
    // trozo a trozo, que pintar medio tramo como si fuera entero.
    if (partes.some(({ data }) => !data?.geoPositionRoads?.length)) {
      return meshFallback(segment, roadId);
    }

    // 🔴 Cada trozo se invierte POR SU CUENTA, y el orden del array no se toca.
    //
    // El servidor siempre devuelve PK ascendente (ver `orderStretchPaths`),
    // así que un trozo recorrido bajando hay que darle la
    // vuelta. Invertir la lista ENTERA al final —lo que se hacía— solo vale
    // cuando hay un trozo: con dos, los trozos vienen en el orden en que se
    // recorrieron y darle la vuelta al conjunto los deja al revés entre sí.
    //
    // Cazado el 2026-08-24 con un recorrido de verdad: bajar de la C-25z
    // (237+100) a la C-25 (82+800) pintaba la vía hasta Cervera y luego un
    // SALTO recto de Cervera a Girona, que sobre la curva del Eix Transversal
    // por Vic dibujaba un lazo cerrado. El camino sin cobertura ya lo hacía
    // bien (`segmentPathFromMesh` invierte cada trozo con SUS extremos), o sea
    // que lo que fallaba era justo lo que se ve a diario.
    return orderStretchPaths(
      rangos,
      partes.map(({ data }) =>
        data.geoPositionRoads.map(
          (p: { latitude: number; longitude: number }) => ({
            lat: p.latitude,
            lng: p.longitude,
          })
        )
      )
    );
  } catch (error) {
    // 🔴 Sin cobertura esta query NO responde, y hasta ahora eso significaba
    // que el tramo simplemente NO se dibujaba: el operario no podía ver por
    // dónde había patrullado justo cuando más lo necesita. La malla de la
    // carretera ya está en la tablet —`ensureRoadMesh` la descarga al arrancar
    // para poder calcular el PK sin red—, así que se recorta de ahí.
    //
    // Es menos preciso que el trazado del servidor (la malla tiene la
    // resolución que tiene y se cachea por carretera, no por sentido), y da
    // igual: la alternativa es no dibujar nada.
    const fallback = meshFallback(segment, roadId);
    if (fallback.length === 0) Sentry.captureException(error);
    return fallback;
  }
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function MapWithSegments({
  segments,
  gpsPoints = [],
  roadId,
  roadWindows,
  pkInitKm,
  pkInitMeter,
  pkEndKm,
  pkEndMeter,
  direction = 'top',
  onSegmentClick,
  selectedSegmentId,
  hideLegend = false,
  livePosition = null,
  liveTrack = [],
  activeSegmentStartDate = null,
  allowFullscreen = false,
}: MapWithSegmentsProps): JSX.Element {
  // 'modals' añadido para reusar `modals:timerOverlay.changeDirection` en la
  // leyenda (fila "cambio de sentido") sin crear una key nueva.
  const { t } = useTranslation(['pages', 'modals']);
  const theme = useTheme();

  const [centerCoordinates, setCenterCoordinates] =
    useState<Coordinates | null>(null);
  const [segmentPaths, setSegmentPaths] = useState<SegmentPath[]>([]);
  const [displayMap, setDisplayMap] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  // Leyenda minimizada por defecto (UX 2026-07-07) — arranca colapsada en
  // un botón redondo pequeño; el usuario la despliega bajo demanda.
  const [legendExpanded, setLegendExpanded] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // PANTALLA COMPLETA (2026-09-01)
  // ═══════════════════════════════════════════════════════════════════
  // Estado LOCAL a propósito: el mapa no se desmonta al expandir (solo cambia
  // el CSS del contenedor), así que no hay nada que preservar fuera. Si la
  // prop se apagase estando expandido, el hook deja de darlo por expandido y
  // el contenedor vuelve a su sitio — no queda un estado atrapado. Escape,
  // «atrás» de Android y el bloqueo del scroll viven en el hook, compartido
  // con el mapa de `components/display/Map`.
  const { fullscreen, toggle: toggleFullscreen } =
    useFullscreenToggle(allowFullscreen);

  // Zoom actual del mapa — lo alimenta el `idle` de Google Maps y decide el
  // umbral de agrupación de los marcadores (ver `dotMarkers`). Arranca en el
  // zoom por defecto del mapa hasta el primer `idle`.
  const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
  // Grupo desplegado "en abanico" (spiderfy) — el id del marcador cuyo grupo
  // está abierto, o null. Solo se usa con grupos que el zoom NO puede separar.
  const [spiderfiedId, setSpiderfiedId] = useState<string | null>(null);
  // Encuadre manual (botón de recentrar y click en un grupo de marcadores).
  // Vive aquí arriba porque `dotMarkers` necesita el setter.
  const [manualFit, setManualFit] = useState<{
    coords: Coordinates[];
    nonce: number;
  } | null>(null);

  // `idle` salta TAMBIÉN al panear; sin el bail de igualdad esto
  // re-renderizaría el mapa entero en cada arrastre.
  const handleViewportChange = useCallback((zoom: number) => {
    setMapZoom((prev) => {
      if (prev === zoom) return prev;
      // Cambiar de zoom cierra el abanico, igual que en las librerías de
      // referencia: las posiciones desplegadas se calculan en píxeles y a otro
      // zoom ya no corresponderían al punto real.
      setSpiderfiedId(null);
      return zoom;
    });
  }, []);

  // Tocar el fondo del mapa cierra el abanico — en táctil no hay Escape, y sin
  // esto la única salida sería pulsar uno de sus marcadores (que selecciona un
  // tramo que quizá no querías).
  const handleMapClick = useCallback(() => {
    setSpiderfiedId(null);
  }, []);

  // Encuadra a los miembros de un grupo de marcadores — el "zoom a los bounds
  // del cluster" del estándar. Reutiliza el mismo `manualFit` + nonce del
  // botón de recentrar (el efecto de fitBounds lleva bail de igualdad, así que
  // sin el nonce un segundo click sobre el mismo grupo no haría nada).
  const zoomToGroup = useCallback((coords: Coordinates[]) => {
    if (coords.length < 2) return;
    setManualFit((prev) => ({ coords, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);

  // Conteo de estados para la leyenda (2 estados de ciclo de vida — ver
  // `getSegmentStatus`: "Pausado" ya no es una línea de leyenda propia).
  const completedCount = segments.filter(
    (s) => getSegmentStatus(s) === 'completed'
  ).length;
  const activeCount = segments.filter(
    (s) => getSegmentStatus(s) === 'active'
  ).length;

  // ═══════════════════════════════════════════════════════════════════
  // CARGAR PATH POR SEGMENTO (paralelo)
  // ═══════════════════════════════════════════════════════════════════

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      if (
        !roadId ||
        pkInitKm === undefined ||
        pkInitMeter === undefined ||
        pkEndKm === undefined ||
        pkEndMeter === undefined ||
        !direction
      ) {
        setMapError(t('surveillance.mapRequiresLocation'));
        setDisplayMap(false);
        return;
      }

      // Si no hay segmentos con PKs cerrados, caemos al path global de la ruta
      // para al menos centrar el mapa en la carretera.
      const hasAnyClosedSegment = segments.some(hasCompletePks);

      try {
        if (hasAnyClosedSegment) {
          const results = await Promise.all(
            segments.map(async (s) => ({
              segmentId: s.id,
              status: getSegmentStatus(s),
              coordinates:
                getSegmentStatus(s) === 'active'
                  ? [] // el activo lo dibujamos con GPS, no con PKs
                  : await loadSegmentCoordinates(
                      s,
                      // Multi-carretera: la del TRAMO. Un tramo antiguo sin
                      // carretera cae a la principal, que es la que tenía.
                      s.roadId ?? roadId,
                      direction
                    ),
            }))
          );
          if (cancelled) return;
          const valid = results.filter((r) => r.coordinates.length > 0);
          setSegmentPaths(valid);

          // Centro = punto medio del primer segmento con coordenadas válidas
          const first = valid[0];
          if (first) {
            const mid = Math.max(
              0,
              Math.round(first.coordinates.length / 2) - 1
            );
            setCenterCoordinates(first.coordinates[mid]);
            setDisplayMap(true);
            setMapError(null);
            return;
          }
        }

        // Fallback: trazado de fondo del recorrido — UNA polilínea por
        // carretera (multi-carretera 2026-08-15). Antes solo se pedía la
        // primera, así que un recorrido de troncal + ramal enseñaba media
        // verdad; y al encuadrar solo con esa mitad, el mapa dejaba la otra
        // fuera de plano.
        //
        // Cada carretera se pide con SU ventana de PK: los kilometrajes de una
        // troncal y su ramal no son comparables, así que filtrar el ramal por
        // el rango de la troncal devolvería un trozo arbitrario.
        const windows =
          roadWindows && roadWindows.length > 0
            ? roadWindows
            : [{ roadId, pkInitKm, pkInitMeter, pkEndKm, pkEndMeter }];

        const trazados = await Promise.all(
          windows.map(async (w) => {
            const { data } = await client.query({
              query: GET_POSITION_ROADS,
              fetchPolicy: 'cache-first',
              variables: {
                input: {
                  pkInitKm: w.pkInitKm,
                  pkInitMeter: w.pkInitMeter,
                  pkEndKm: w.pkEndKm,
                  pkEndMeter: w.pkEndMeter,
                  roadId: w.roadId,
                  direction: direction === 'both' ? 'top' : direction,
                },
              },
            });
            const coords: Coordinates[] = (data?.geoPositionRoads ?? []).map(
              (p: { latitude: number; longitude: number }) => ({
                lat: p.latitude,
                lng: p.longitude,
              })
            );
            return { roadId: w.roadId, coords };
          })
        );
        if (cancelled) return;

        // Una carretera sin malla no tumba al resto: se dibuja lo que haya.
        const conTrazado = trazados.filter((tr) => tr.coords.length > 0);

        if (conTrazado.length > 0) {
          const primero = conTrazado[0].coords;
          const mid = Math.max(0, Math.round(primero.length / 2) - 1);
          setCenterCoordinates(primero[mid]);
          // Trazado global de la ruta (sin desglose por tramo) — `status:
          // 'completed'` es un valor dummy que el type `SegmentPath` exige,
          // pero NUNCA se lee como estado real de patrulla: `paths` useMemo
          // resuelve el color de este id aparte (gris neutro vía
          // ROUTE_FALLBACK_SEGMENT_ID, ver su comentario) precisamente para
          // NO implicar "completado" cuando ningún tramo ha sido patrullado.
          //
          // El id lleva sufijo por carretera para que React pueda distinguir
          // las polilíneas, pero TODAS empiezan por `ROUTE_FALLBACK_SEGMENT_ID`
          // — que es lo que miran el color gris y el filtro del tramo activo.
          setSegmentPaths(
            conTrazado.map((tr) => ({
              segmentId:
                conTrazado.length === 1
                  ? ROUTE_FALLBACK_SEGMENT_ID
                  : `${ROUTE_FALLBACK_SEGMENT_ID}:${tr.roadId}`,
              status: 'completed' as const,
              coordinates: tr.coords,
            }))
          );
          setDisplayMap(true);
          setMapError(null);
        } else {
          setMapError(t('surveillance.noGeoPositionsFound'));
          setDisplayMap(false);
        }
      } catch (error) {
        if (cancelled) return;
        Sentry.captureException(error);
        console.error('Error loading segment paths:', error);
        setMapError(t('surveillance.errorLoadingMap'));
        setDisplayMap(false);
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [
    segments,
    roadId,
    roadWindows,
    pkInitKm,
    pkInitMeter,
    pkEndKm,
    pkEndMeter,
    direction,
    t,
  ]);

  // ═══════════════════════════════════════════════════════════════════
  // GPS TRACK del segmento activo (overlay)
  // ═══════════════════════════════════════════════════════════════════

  const gpsTrackPath = useMemo<Coordinates[]>(
    () =>
      gpsPoints.map((p) => ({
        lat: p.latitude,
        lng: p.longitude,
      })),
    [gpsPoints]
  );

  // Última señal GPS (control-room value: "¿sigue viva la patrulla?") — UX
  // 2026-07-07 reemplaza el conteo crudo de puntos ("3983 puntos", telemetría
  // de desarrollo sin valor operacional) por la hora del ÚLTIMO punto
  // recibido. Se calcula por `timestamp` máximo (no por posición en el
  // array) para ser robusto aunque el backend no garantice orden estricto.
  const lastGpsSignalLabel = useMemo<string | null>(() => {
    if (gpsPoints.length === 0) return null;
    const last = gpsPoints.reduce((latest, p) =>
      new Date(p.timestamp).getTime() > new Date(latest.timestamp).getTime()
        ? p
        : latest
    );
    const pointDate = new Date(last.timestamp);
    if (Number.isNaN(pointDate.getTime())) return null;
    const isToday = pointDate.toDateString() === new Date().toDateString();
    return format(pointDate, isToday ? 'HH:mm' : 'dd/MM HH:mm');
  }, [gpsPoints]);

  // Si aún no tenemos centro (sin roadId válido) pero sí GPS, centrar ahí
  useEffect(() => {
    if (!centerCoordinates && gpsTrackPath.length > 0) {
      const mid = Math.floor(gpsTrackPath.length / 2);
      setCenterCoordinates(gpsTrackPath[mid]);
      setDisplayMap(true);
    }
  }, [centerCoordinates, gpsTrackPath]);

  // ═══════════════════════════════════════════════════════════════════
  // BUILD paths[] para MultiPathMap
  // ═══════════════════════════════════════════════════════════════════

  // Follow-up 2026-07-08 (mapa→selección desde el propio trazado): lookup
  // de segmento por id para colgar el `onClick` de cada polyline de tramo.
  // El pseudo-path `'route-fallback'` (ruta completa sin desglose por
  // tramo) NO resuelve aquí → queda sin onClick → no clicable, igual que
  // la traza GPS (que ni siquiera pasa por este map, ver abajo).
  const segmentById = useMemo(() => {
    const byId = new Map<string, RouteSegment>();
    segments.forEach((s) => byId.set(s.id, s));
    return byId;
  }, [segments]);

  // Traza AZUL del tramo activo (Task A3, 2026-07-23) — bug "línea azul
  // persistente" (Caso 1 del plan): antes se pintaba TODO `gpsPoints` del
  // recorrido completo (`gpsTrackPath` abajo), así que la línea azul nunca
  // desaparecía tras cerrar el primer tramo. Ahora se acota a la ventana del
  // tramo EN CURSO: puntos ya persistidos en backend con
  // `timestamp >= activeSegmentStartDate`, concatenados con `liveTrack` (el
  // estado vivo del tracker, Task A2 — dedup por timestamp para no repetir
  // un punto que YA llegó por refetch del backend). Sin tramo activo
  // (`activeSegmentStartDate` null → ruta pausada/cerrada) el resultado es
  // `[]` — no hay línea azul, solo los tramos cerrados en verde vía PKs.
  const activeTrack = useMemo<Coordinates[]>(() => {
    if (!activeSegmentStartDate) return [];
    const start = new Date(activeSegmentStartDate).getTime();
    const backendPoints = gpsPoints
      .filter((p) => new Date(p.timestamp).getTime() >= start)
      .map((p) => ({
        lat: p.latitude,
        lng: p.longitude,
        timestamp: p.timestamp,
      }));
    const seenTimestamps = new Set(backendPoints.map((p) => p.timestamp));
    // Fix 2026-07-23 (review): un "canvi de sentit" (finishSegment+createSegment
    // con `route.state` que se mantiene 'in-progress' y `routeId` sin cambiar)
    // no re-dispara el effect de tracking → `liveTrack` puede seguir arrastrando
    // puntos del tramo ANTERIOR (timestamp < `start`). Sin esta guarda, esos
    // puntos se concatenan y la línea azul retrocede/zigzaguea. Se acota
    // `liveTrack` a la misma ventana que `backendPoints`.
    const livePoints = (liveTrack ?? []).filter(
      (p) =>
        new Date(p.timestamp).getTime() >= start && !seenTimestamps.has(p.timestamp)
    );
    // Fix "la línea azul retrocede al sincronizar" (pruebas de campo C-25,
    // 2026-07-28): `backendPoints` (de `gpsPoints`, que el backend puede
    // devolver DECIMADO) y `livePoints` (traza viva completa) cubren AMBOS
    // toda la ventana del tramo activo. Concatenarlos SIN ordenar dibujaba
    // primero la traza del servidor y LUEGO saltaba al inicio a redibujar la
    // viva → la polyline "iba hacia atrás". El salto era invisible mientras se
    // estaba offline (`backendPoints` vacío → solo la viva), pero al
    // re-fetchear `gpsPoints` tras sincronizar la cola offline el backend pasa
    // de vacío a poblado y el zigzag aparecía. Ordenar por timestamp garantiza
    // una polyline SIEMPRE cronológica (monótona), sea cual sea el orden o la
    // decimación con que el backend devuelva los puntos y dónde caiga la
    // frontera backend/vivo. (`seenTimestamps` ya evita duplicar por timestamp
    // exacto; el sort resuelve el orden del resto.)
    return [...backendPoints, ...livePoints]
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      .map((p) => ({
        lat: p.lat,
        lng: p.lng,
      }));
  }, [activeSegmentStartDate, gpsPoints, liveTrack]);

  const paths = useMemo<MultiPathEntry[]>(() => {
    const hasSelection = Boolean(selectedSegmentId);
    const out: MultiPathEntry[] = segmentPaths
      // Ruta INICIADA (hay tramo activo): NO dibujar la línea gris del
      // recorrido entero (`ROUTE_FALLBACK_SEGMENT_ID`). Ese gris es solo un
      // trazado TEMPORAL de referencia para ubicarte ANTES de empezar; una
      // vez iniciado el primer tramo taparía la traza azul en curso. Se
      // mantiene dentro de `segmentPaths` (→ `fitBoundsCoordinates` sigue
      // encuadrando el recorrido completo), pero no se pinta: mandan la flecha
      // + el azul del tramo que se está recorriendo. Sin tramo activo (ruta
      // `open`, aún sin iniciar) el gris SÍ se pinta.
      .filter(
        (sp) =>
          !(
            isRouteFallbackPath(sp.segmentId) &&
            !!activeSegmentStartDate
          )
      )
      .map((sp) => {
      const isSelected = hasSelection && sp.segmentId === selectedSegmentId;
      const segment = segmentById.get(sp.segmentId);
      return {
        id: sp.segmentId,
        coordinates: sp.coordinates,
        // Bug 2026-07-09: el path de fallback (recorrido completo sin
        // desglose por tramo, ver ROUTE_FALLBACK_SEGMENT_ID) NO es un tramo
        // patrullado — pintarlo con `routeStatus[sp.status]` (verde, por el
        // valor dummy `status: 'completed'` de `loadAll`) mentía "ya
        // patrullado". Gris neutro, distinto de los 3 colores de estado
        // real (verde/naranja/azul) y de las capas de énfasis
        // (`black`/`white`) — se lee como "trazado de referencia", no como
        // un tramo con estado.
        color:
          isRouteFallbackPath(sp.segmentId)
            ? theme.colors.greyDark
            : theme.colors.routeStatus[sp.status],
        // Tabla→mapa (2026-07-08): tram seleccionado más grueso (+3px) y
        // con la casing emphasized (ver MultiPathMap); el resto se atenúa
        // a ~0.45 de opacidad para que el seleccionado destaque sin
        // desaparecer del todo. Sin selección, todos vuelven a su trazo
        // normal (comportamiento previo, byte-idéntico).
        strokeWeight: isSelected ? SEGMENT_STROKE_WEIGHT_SELECTED : SEGMENT_STROKE_WEIGHT,
        // Un tramo que aún espera en la cola se pinta más flojo, se haya
        // seleccionado o no: que el trabajo se vea, pero que no aparente estar
        // confirmado. Se combina con la atenuación por selección tomando la
        // MENOR de las dos — si además está sin seleccionar, no se suman.
        strokeOpacity: Math.min(
          hasSelection && !isSelected ? DIMMED_STROKE_OPACITY : 1,
          (segment as (RouteSegment & { isPending?: boolean }) | undefined)
            ?.isPending
            ? PENDING_STROKE_OPACITY
            : 1
        ),
        emphasized: isSelected,
        casingColor: isSelected ? theme.colors.black : undefined,
        // Sentido visible SOLO en el tram seleccionado (mismo mecanismo que
        // la traza GPS activa) — las coordenadas ya vienen en orden real de
        // patrulla (ver `orderStretchPaths` en `loadSegmentCoordinates`).
        directionArrows: isSelected,
        // Bug 2026-07-09 (flechas invisibles tras 3b7648b1): `MultiPathMap`
        // soporta `directionArrowColor` desde 3b7648b1 (pensado para
        // contrastar con la línea sólida — ver su comentario ahí), pero
        // NINGÚN consumidor lo pasaba; sin él, `arrowColor` caía en
        // `entry.color` — el MISMO verde/azul que la línea seleccionada
        // (opaca, 10px) bajo la flecha (~9.6px de ancho) → mismo color
        // sobre mismo color, invisible. Blanco contrasta en los 2 estados
        // reales (`routeStatus.completed` verde / `active` azul) y en
        // ambos temas (claro: blanco real; oscuro: `white` se invierte a
        // casi-negro — también contraste sobre el verde/azul de tema
        // oscuro).
        directionArrowColor: theme.colors.white,
        // Click/tap sobre el trazado del tram = mismo handler (y mismo
        // toggle) que la fila de la tabla y el dot de pausa. MultiPathMap
        // materializa esto como una hit-polyline invisible más ancha
        // (~16px, táctil) — ver `HIT_AREA_*` allí.
        onClick:
          segment && onSegmentClick
            ? () => onSegmentClick(segment)
            : undefined,
      };
    });

    // Gate del Caso 1 (ver comentario de `activeTrack` arriba): SOLO hay
    // línea azul con un tramo activo Y al menos 2 puntos en su ventana.
    if (activeTrack.length > 1) {
      out.push({
        id: 'gps-track-active',
        coordinates: activeTrack,
        color: theme.colors.routeStatus.active,
        strokeWeight: 5,
        strokeOpacity: 0.75,
        dashed: true,
        directionArrows: true,
      });
    }
    return out;
  }, [
    segmentPaths,
    activeSegmentStartDate,
    activeTrack,
    theme,
    selectedSegmentId,
    segmentById,
    onSegmentClick,
  ]);

  // Coordenadas SOLO de los tramos (+ fallback de ruta completa cuando no
  // hay desglose por segmento) — la traza GPS NUNCA entra aquí, para que
  // los outliers GPS de campo no expandan el encuadre del mapa.
  const fitBoundsCoordinates = useMemo<Coordinates[]>(
    () => segmentPaths.flatMap((sp) => sp.coordinates),
    [segmentPaths]
  );

  // Cursor GPS (Task A3, 2026-07-23) — Casos 4/6 del plan (flecha ausente al
  // arrancar con red débil / tras cambiar de sentido): con `livePosition`
  // disponible (estado vivo del tracker, Task A2) el cursor aparece AL
  // INSTANTE de la primera lectura aceptada, sin esperar el flush (15s) ni
  // un refetch de `gpsPoints` del backend. El rumbo prioriza
  // `livePosition.heading` (del propio sensor GPS); si el navegador no lo
  // reporta, se calcula por bearing entre el punto vivo anterior
  // (`liveTrack`) y el actual — o, si `liveTrack` aún no tiene 2 puntos
  // (primerísima lectura de un tracking recién arrancado), contra el último
  // punto histórico de `gpsTrackPath`. Sin `livePosition` (roles de solo
  // lectura, o antes del primer fix GPS) se conserva el fallback previo:
  // último punto de `gpsPoints` del backend.
  const cursorMarkers = useMemo<MultiPathMarker[]>(() => {
    const buildCursor = (
      position: Coordinates,
      rotationDeg: number | undefined
    ): MultiPathMarker => ({
      id: 'cursor',
      position,
      rotationDeg,
      // Cursor GPS = flecha de navegación (icono Lucide "navigation-2"
      // rotado al rumbo, o punto de respaldo sin flecha con 1 solo
      // punto GPS). Discriminador explícito e independiente de
      // rotationDeg — ver _internal.createCursorArrowContent /
      // createCursorDotContent.
      cursorIcon: true,
      icon: {
        // `path` solo se usa como type-guard en MultiPathMap (`'path'
        // in m.icon`) para distinguir Symbol de Icon — el contenido
        // real del cursor lo decide `cursorIcon`, no este path.
        path: DOT_SYMBOL_PATH,
        // Color propio (garnet), DISTINTO del color de las líneas de
        // traza GPS (`routeStatus.active`) — así el cursor se
        // distingue de la traza que dibuja encima.
        strokeColor: theme.colors.garnet,
        fillColor: theme.colors.garnet,
        strokeWeight: 1,
        strokeOpacity: 1,
        fillOpacity: 1,
      } as google.maps.Symbol,
    });

    if (livePosition) {
      const live: Coordinates = {
        lat: livePosition.lat,
        lng: livePosition.lng,
      };
      const liveTrackCoords = (liveTrack ?? []).map((p) => ({
        lat: p.lat,
        lng: p.lng,
      }));
      const prevLive: Coordinates | null =
        liveTrackCoords.length > 1
          ? liveTrackCoords[liveTrackCoords.length - 2]
          : gpsTrackPath.length > 0
            ? gpsTrackPath[gpsTrackPath.length - 1]
            : null;
      const rotationDeg =
        livePosition.heading ??
        (prevLive && (prevLive.lat !== live.lat || prevLive.lng !== live.lng)
          ? computeBearing(prevLive, live)
          : undefined);
      return [buildCursor(live, rotationDeg)];
    }

    if (gpsTrackPath.length === 0) return [];
    const last = gpsTrackPath[gpsTrackPath.length - 1];
    const prev =
      gpsTrackPath.length > 1 ? gpsTrackPath[gpsTrackPath.length - 2] : null;
    const rotationDeg =
      prev && (prev.lat !== last.lat || prev.lng !== last.lng)
        ? computeBearing(prev, last)
        : undefined;
    return [buildCursor(last, rotationDeg)];
  }, [livePosition, liveTrack, gpsTrackPath, theme]);

  // ═══════════════════════════════════════════════════════════════════
  // MARCADOR FUSIONADO: PAUSA + CAMBIO DE SENTIDO (fleet-timeline standard)
  //
  // Feedback usuario 2026-07-07: el detalle de la causa de pausa vive en la
  // tabla de Tramos, pero el DÓNDE se paró la patrulla y el DÓNDE cambió de
  // sentido se leían mejor en el propio mapa. Reintroducidos como point
  // markers (no como color de línea — ver `getSegmentStatus` arriba, esa
  // semántica es del ciclo de vida de la ruta, no de la pausa).
  //
  // Feedback usuario 2026-07-09 (fusión): el fin de un tram CERRADO y el
  // inicio del SIGUIENTE tram cerrado son la MISMA coordenada física — así
  // que, cuando ese punto era también un cambio de sentido, el dot de
  // pausa (color de causa) y el dot de cambio de sentido (negro, aparte)
  // quedaban exactamente superpuestos. `spreadOverlappingMarkers` los
  // separaba en corona, pero el usuario lo encontró feo y pidió UN SOLO
  // marcador: el relleno es SIEMPRE el color de la causa de pausa
  // (`getPauseCauseColor`) y el glifo ↺ se dibuja DENTRO (reutilizando el
  // MISMO `directionChangeIcon`/`createDirectionChangeMarkerContent` de
  // `Map/_internal.tsx` que antes solo alimentaba el dot negro — ese
  // renderer ya acepta cualquier `fillColor`, no solo `black`) SOLO cuando
  // el tram siguiente cronológicamente cambia de sentido. Sin cambio de
  // sentido, el marcador es el punto liso de siempre.
  // ═══════════════════════════════════════════════════════════════════

  // Tramos CERRADOS en orden cronológico real (por `startDate`) — mismo
  // criterio que `SegmentList` (ver su comentario: el orden por `indexType`
  // reiniciaba la numeración por sentido).
  const closedSegmentsChrono = useMemo<RouteSegment[]>(
    () =>
      segments
        .filter((s) => Boolean(s.endDate))
        .slice()
        .sort(
          (a, b) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        ),
    [segments]
  );

  // Coordenadas ya cargadas por tramo (mismo `segmentPaths` que alimenta
  // `fitBoundsCoordinates` — los markers de pausa/cambio de sentido colocan
  // sus puntos en el primer/último punto de esas MISMAS coordenadas, así
  // que nunca expanden el encuadre más allá de lo que ya expandían los
  // tramos).
  const segmentCoordinatesById = useMemo(() => {
    const byId = new Map<string, Coordinates[]>();
    segmentPaths.forEach((sp) => byId.set(sp.segmentId, sp.coordinates));
    return byId;
  }, [segmentPaths]);

  // Causas de pausa REALMENTE presentes en esta ruta (para la leyenda) —
  // basado solo en datos de segmento, independiente de si las coordenadas
  // ya cargaron (evita que la leyenda "parpadee" vacía mientras
  // `GET_POSITION_ROADS` resuelve).
  const pauseCausesPresent = useMemo(
    () =>
      PAUSE_CAUSE_ORDER.filter((cause) =>
        closedSegmentsChrono.some((s) => s.pauseCause === cause)
      ),
    [closedSegmentsChrono]
  );

  // TODOS los tramos en orden cronológico real (por `startDate`), incluido el
  // ACTIVO — a diferencia de `closedSegmentsChrono`, que filtra por `endDate`.
  // Solo lo consume la detección de cambio de sentido de abajo; el resto de
  // consumidores (leyenda, dots de pausa) siguen mirando solo los cerrados,
  // porque un tramo abierto todavía no tiene ni causa ni punto final que pintar.
  const allSegmentsChrono = useMemo<RouteSegment[]>(
    () =>
      segments
        .slice()
        .sort(
          (a, b) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        ),
    [segments]
  );

  // Ids de tramos CERRADOS cuyo FINAL es un punto de cambio de sentido — el
  // tram SIGUIENTE cronológicamente tiene un sentido NORMALIZADO distinto.
  // Compara vía `directionNameKey` (no el string crudo) para no disparar
  // falsos positivos entre vocabulario nuevo/legado (`top` vs `ascending` son
  // el MISMO sentido, ver import de arriba). Indexado por el tram que TERMINA
  // en el punto de cambio (para poder fusionar el glifo dentro de SU dot de
  // pausa, ver `pauseMarkers` abajo) en vez del que empieza.
  //
  // Bug 2026-08-10 (feedback usuario, «en tiempo real y con poca cobertura no
  // aparece el iconito»): antes se recorría `closedSegmentsChrono`, así que el
  // "siguiente" tenía que estar CERRADO también. Pero el tram que nace de un
  // «Canvi de sentit» (`TimerOverlay.handleChangeDirection`, patrón
  // finish+create) está ABIERTO y es justo el que lleva el sentido NUEVO —
  // el giro era invisible durante todo el tramo siguiente, es decir
  // exactamente en el momento en que el operario mira el mapa. Con poca
  // cobertura era peor: `createSegment` se encola y devuelve `null` sin
  // escribir nada optimista, así que el tram nuevo no existía en `segments`
  // hasta que drenaba la cola. Recorrer TODOS los tramos (el activo es
  // siempre el último cronológico) hace aparecer el ↺ en cuanto se registra
  // el giro. El `if (!segment.endDate) return` mantiene la otra mitad de la
  // regla: el glifo vive en el punto FINAL de un tram cerrado, y un tram
  // abierto no tiene punto final que marcar.
  const directionChangeAtEndSegmentIds = useMemo(() => {
    const ids = new Set<string>();
    allSegmentsChrono.forEach((segment, idx) => {
      if (!segment.endDate) return;
      const next = allSegmentsChrono[idx + 1];
      if (!next) return;
      const currKey = directionNameKey(segment.direction);
      const nextKey = directionNameKey(next.direction);
      if (Boolean(currKey) && Boolean(nextKey) && currKey !== nextKey) {
        ids.add(segment.id);
      }
    });
    return ids;
  }, [allSegmentsChrono]);

  // Un punto por tram cerrado, en orden CRONOLÓGICO: el que tiene causa de
  // pausa lleva su color (el caso normal — el workflow siempre cierra con
  // causa), y el que no la tiene solo aparece si además cambia de sentido
  // (fallback defensivo: hoy el único close sin causa es el fin de ruta
  // explícito de `useRouteTimerActions.handleFinishRoute`, que por
  // construcción es el ÚLTIMO tram y por tanto nunca gira; se mantiene por si
  // el workflow cambia o hay datos legacy). Todavía NO son marcadores: varios
  // pueden caer en la misma coordenada y se agrupan justo debajo.
  const segmentDots = useMemo<SegmentDot[]>(
    () =>
      closedSegmentsChrono
        .map((segment): SegmentDot | null => {
          const coords = segmentCoordinatesById.get(segment.id);
          if (!coords || coords.length === 0) return null;
          const hasGlyph = directionChangeAtEndSegmentIds.has(segment.id);
          if (!segment.pauseCause && !hasGlyph) return null;
          return {
            segment,
            position: coords[coords.length - 1],
            color: segment.pauseCause
              ? getPauseCauseColor(segment.pauseCause, theme)
              : theme.colors.black,
            hasGlyph,
          };
        })
        .filter((d): d is SegmentDot => d !== null),
    [
      closedSegmentsChrono,
      segmentCoordinatesById,
      directionChangeAtEndSegmentIds,
      theme,
    ]
  );

  // ═══════════════════════════════════════════════════════════════════
  // CONVERGENCIA (2026-08-10) — UN marcador por COORDENADA
  //
  // Un recorrido de vigilancia va y vuelve por la misma vía, y los PKs se
  // teclean sobre valores redondos, así que varios tramos terminan en el
  // MISMO punto: es la forma NORMAL de un recorrido, no un caso raro
  // (Su.0000323 tiene 8 tramos y 2 acaban en el 160+000).
  //
  // Antes se dibujaba un marcador por tramo y `spreadOverlappingMarkers` los
  // repartía en corona con un radio FIJO en grados (0,00003 ≈ 3,3 m). A la
  // escala de un recorrido entero (~55 m/píxel) eso son 0,06 píxeles: los
  // marcadores quedaban clavados uno encima de otro y el mapa enseñaba menos
  // paradas de las que hubo — y escondía cambios de sentido (feedback usuario
  // 2026-08-10 sobre Su.0000326 y Su.0000323). Solo habría separado algo con
  // el zoom a nivel de calle.
  //
  // Se agrupa en DOS niveles, en este orden:
  //   1. IDENTIDAD — misma coordenada exacta. No depende del zoom: por mucho
  //      que acerques, esos tramos nunca se separan.
  //   2. PROXIMIDAD EN PANTALLA — los puntos que caen a menos de 60 px al zoom
  //      actual. Sin esta capa, el mapa a la escala de un recorrido entero
  //      seguía siendo un amasijo: a zoom 11 los 23 puntos de Su.0000336 caen
  //      a ~50 px de media y las chapitas se tapaban unas a otras. Se agrupa
  //      por DISTANCIA entre puntos y no por celda de rejilla, para que
  //      panear no recomponga los grupos (el parpadeo que la propia doc de
  //      Google le reprocha al `GridAlgorithm`).
  //
  // Y la ambigüedad se resuelve por SELECCIÓN, no separando en el espacio (la
  // corona ya se rechazó en julio):
  //   · Sin selección → una sola causa en el grupo pinta su color; varias,
  //     color neutro (mezclar colores de paradas DISTINTAS no informa de
  //     nada). La chapita dice cuántas son y el ↺ aparece si alguna gira.
  //   · Con un tramo del grupo seleccionado → el dot pasa a ser SU marcador
  //     real (su color, su ↺ o su ausencia) con el anillo de selección, y la
  //     chapita se queda para no perder el "aquí hay más".
  //   · Pulsando el punto → si el grupo se puede separar acercando, encuadra
  //     a sus miembros (estándar del sector); si no (todos en la misma
  //     coordenada), cicla al siguiente tramo, y la tabla de Tramos hace de
  //     enumerador porque ya se desplaza sola a la fila seleccionada.
  //
  // El cursor GPS queda fuera: es único, con icono propio (flecha), y nunca
  // compite por el mismo punto que un dot de pausa.
  // ═══════════════════════════════════════════════════════════════════
  const markerClusters = useMemo<SegmentDot[][]>(() => {
    // Nivel 1 — identidad.
    const byCoordinate = new Map<string, SegmentDot[]>();
    segmentDots.forEach((dot) => {
      const key = `${dot.position.lat.toFixed(6)},${dot.position.lng.toFixed(6)}`;
      const existing = byCoordinate.get(key);
      if (existing) existing.push(dot);
      else byCoordinate.set(key, [dot]);
    });
    const points = Array.from(byCoordinate.values());

    // Nivel 2 — proximidad en pantalla, al zoom actual.
    return clusterByScreenDistance(
      points.map((miembros) => ({ position: miembros[0].position, miembros })),
      mapZoom
    ).map((grupo) => grupo.flatMap((p) => p.miembros));
  }, [segmentDots, mapZoom]);

  /** Id del marcador de un grupo — estable mientras su primer miembro lidere. */
  const clusterMarkerId = (group: SegmentDot[]) => `dot-${group[0].segment.id}`;

  /** ¿Puede el zoom separar este grupo? Solo si NO todos comparten coordenada. */
  const isSeparable = (group: SegmentDot[]) =>
    group.some(
      (d) =>
        d.position.lat !== group[0].position.lat ||
        d.position.lng !== group[0].position.lng
    );

  const dotMarkers = useMemo<MultiPathMarker[]>(() => {
    return markerClusters.flatMap((group): MultiPathMarker[] => {
      // ── ABANICO ABIERTO ──────────────────────────────────────────────
      // Este grupo comparte coordenada y el usuario lo ha abierto: en vez de
      // UN marcador con un contador al que no se puede llegar, se despliegan
      // los N reales alrededor del punto (cada uno con SU color y SU glifo) y
      // las líneas guía de abajo dicen de dónde salen. Pulsar cualquiera lo
      // selecciona y cierra el abanico — el mismo comportamiento por defecto
      // que `OverlappingMarkerSpiderfier`.
      if (spiderfiedId === clusterMarkerId(group) && group.length > 1) {
        const posiciones = spiderfyPositions(
          group[0].position,
          group.length,
          mapZoom
        );
        return group.map((dot, i) => ({
          id: `spider-${dot.segment.id}`,
          position: posiciones[i],
          directionChangeIcon: dot.hasGlyph,
          selectedOutline: dot.segment.id === selectedSegmentId,
          accentColor: theme.colors.black,
          icon: {
            path: DOT_SYMBOL_PATH,
            fillColor: dot.color,
            strokeColor: theme.colors.white,
            strokeWeight: 2,
            fillOpacity: 1,
            strokeOpacity: 1,
          } as google.maps.Symbol,
          onClick: () => {
            setSpiderfiedId(null);
            onSegmentClick?.(dot.segment);
          },
        }));
      }

      return [buildClusterMarker(group)];
    });

    function buildClusterMarker(group: SegmentDot[]): MultiPathMarker {
      const selectedIndex = group.findIndex(
        (d) => d.segment.id === selectedSegmentId
      );
      const selected = selectedIndex >= 0 ? group[selectedIndex] : null;
      // Ciclado: sin selección previa, el primero cronológico; con ella, el
      // siguiente del grupo dando la vuelta.
      const target =
        selectedIndex >= 0
          ? group[(selectedIndex + 1) % group.length]
          : group[0];
      const distinctColors = Array.from(new Set(group.map((d) => d.color)));
      return {
        // `group[0]` es el primero cronológico — id estable mientras ese
        // miembro siga liderando el grupo (evita remontes al cambiar el zoom).
        id: clusterMarkerId(group),
        position: group[0].position,
        directionChangeIcon: selected
          ? selected.hasGlyph
          : group.some((d) => d.hasGlyph),
        // Varias causas en el grupo → neutro. Solo el color que se puede
        // afirmar se afirma.
        neutralFill: !selected && distinctColors.length > 1,
        badgeCount: group.length,
        selectedOutline: Boolean(selected),
        accentColor: theme.colors.black,
        icon: {
          path: DOT_SYMBOL_PATH,
          fillColor: (selected ?? group[0]).color,
          strokeColor: theme.colors.white,
          strokeWeight: 2,
          fillOpacity: 1,
          strokeOpacity: 1,
        } as google.maps.Symbol,
        onClick: () => {
          if (group.length > 1) {
            // Grupo que el zoom PUEDE separar → encuadrarlo, que es lo que
            // hace el sector al pulsar un cluster. Reutiliza la mecánica del
            // botón de recentrar (mismo `manualFit` + nonce).
            if (isSeparable(group)) {
              zoomToGroup(group.map((d) => d.position));
              return;
            }
            // Coordenada idéntica: por mucho zoom que se haga no se separan
            // nunca, así que el contador señalaba unos tramos a los que no
            // había forma de llegar. Se abre en abanico — el comportamiento
            // por defecto de `Leaflet.markercluster` para este caso exacto.
            setSpiderfiedId(clusterMarkerId(group));
            return;
          }
          onSegmentClick?.(target.segment);
        },
      };
    }
  }, [
    markerClusters,
    spiderfiedId,
    selectedSegmentId,
    mapZoom,
    theme,
    onSegmentClick,
    zoomToGroup,
  ]);

  // Líneas guía del abanico: del punto real a cada marcador desplegado, para
  // que se lea que están DESPLAZADOS y no en su sitio. Son el `leg` del
  // spiderfy de referencia (allí `legWeight: 1.5`).
  const spiderLegs = useMemo<MultiPathEntry[]>(() => {
    const group = markerClusters.find(
      (g) => clusterMarkerId(g) === spiderfiedId && g.length > 1
    );
    if (!group) return [];
    return spiderfyPositions(group[0].position, group.length, mapZoom).map(
      (destino, i) => ({
        id: `spider-leg-${group[i].segment.id}`,
        coordinates: [group[0].position, destino],
        color: theme.colors.black,
        strokeWeight: 2,
        strokeOpacity: 0.55,
      })
    );
  }, [markerClusters, spiderfiedId, mapZoom, theme]);

  const markers = useMemo<MultiPathMarker[]>(
    () => [...cursorMarkers, ...dotMarkers],
    [cursorMarkers, dotMarkers]
  );

  // Las patas del abanico van AL FINAL para pintarse encima de los tramos.
  const pathsWithLegs = useMemo<MultiPathEntry[]>(
    () => (spiderLegs.length > 0 ? [...paths, ...spiderLegs] : paths),
    [paths, spiderLegs]
  );

  // ═══════════════════════════════════════════════════════════════════
  // BOTÓN DE RECENTRAR (2026-07-08)
  //
  // Estándar de apps de mapas: un botón "locate" en la pila de controles
  // de la esquina (encima del toggle de leyenda). Con un tram SELECCIONADO
  // encuadra ese tram (padding 60px, "se ve todo el tramo"); sin selección
  // encuadra el recorrido completo (mismas coordenadas que el encuadre
  // inicial). Reutiliza la mecánica `fitBoundsCoordinates` existente: el
  // efecto de fitBounds de `_internal.Map` lleva un bail de igualdad
  // (mismas coords → no re-encuadra, protege el pan/zoom manual), así que
  // cada click incrementa un NONCE que entra en esa clave — re-clicar
  // re-encuadra aunque las coordenadas no hayan cambiado. El estado se
  // resetea cuando cambian los datos de tramos (`segmentPaths`) para que
  // el encuadre por defecto vuelva a aplicar con datos frescos (y ninguna
  // recarga re-encuadre a coordenadas obsoletas del último click).
  // ═══════════════════════════════════════════════════════════════════

  useEffect(() => {
    setManualFit(null);
  }, [segmentPaths]);

  const handleRecenter = useCallback(() => {
    const selectedCoords = selectedSegmentId
      ? segmentCoordinatesById.get(selectedSegmentId)
      : undefined;
    const target =
      selectedCoords && selectedCoords.length >= 2
        ? selectedCoords
        : fitBoundsCoordinates;
    if (target.length < 2) return;
    setManualFit((prev) => ({
      coords: target,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  }, [selectedSegmentId, segmentCoordinatesById, fitBoundsCoordinates]);

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  return (
    <MapContainer $fullscreen={fullscreen} data-fullscreen={fullscreen}>
      {/*
        Telemetría de test (Task A4, 2026-07-23) — el canvas de Google Maps
        no es inspeccionable por Playwright, así que estos nodos ocultos
        exponen el estado derivado que SÍ importa verificar: ¿hay cursor GPS
        en vivo?, ¿hay traza azul del tramo activo? No son UI — no llevan
        texto ni aria, y no deben leerse en producción. Ver
        `tests/e2e/specs/surveillanceRoute/gps-live-render.spec.ts`.

        `active-track-count` (review 2026-07-23, fix "línea azul retrocede
        tras canvi de sentit"): expone `activeTrack.length` en texto para que
        el spec pueda comprobar el número EXACTO de vértices en vez de solo
        presencia/ausencia — así queda cubierto que los puntos previos al
        `start` de la ventana (backend Y liveTrack) quedan fuera.
      */}
      {livePosition && (
        <span style={{ display: 'none' }} data-testid="gps-cursor-present" />
      )}
      {activeTrack.length > 1 && (
        <span style={{ display: 'none' }} data-testid="active-track-present" />
      )}
      <span style={{ display: 'none' }} data-testid="active-track-count">
        {activeTrack.length}
      </span>
      {/*
        `direction-change-marker-count` (2026-08-10): número de dots que
        llevan el glifo ↺ dentro. El glifo se dibuja en el canvas de Google
        Maps (`createDirectionChangeMarkerContent`), que Playwright no puede
        inspeccionar — sin este nodo el marcador fusionado no tenía NINGÚN
        gate, que es como el bug "el ↺ no aparece en vivo" llegó a producción.
        Ver `map-direction-change-marker.spec.ts`.
      */}
      <span
        style={{ display: 'none' }}
        data-testid="direction-change-marker-count"
      >
        {dotMarkers.filter((m) => m.directionChangeIcon).length}
      </span>
      {/*
        Convergencia (2026-08-10): `dot-marker-count` es cuántos PUNTOS se
        dibujan y `converged-marker-count` cuántos representan a más de un
        tramo. Juntos prueban la agrupación por coordenada — con 8 tramos que
        acaban en 7 PKs distintos, 7 y 1. `selected-dot-fill` expone el color
        del dot que representa al tramo seleccionado, que es lo que cambia al
        ir pulsando tramos que convergen en el mismo sitio.
      */}
      <span style={{ display: 'none' }} data-testid="dot-marker-count">
        {dotMarkers.length}
      </span>
      <span style={{ display: 'none' }} data-testid="spider-leg-count">
        {spiderLegs.length}
      </span>
      <span style={{ display: 'none' }} data-testid="converged-marker-count">
        {dotMarkers.filter((m) => (m.badgeCount ?? 0) > 1).length}
      </span>
      <span style={{ display: 'none' }} data-testid="selected-dot-fill">
        {dotMarkers
          .filter((m) => m.selectedOutline)
          .map((m) =>
            m.icon && 'path' in m.icon ? String(m.icon.fillColor ?? '') : ''
          )
          .join(',')}
      </span>
      {displayMap && centerCoordinates ? (
        <MapWrapper>
          <MultiPathMap
            center={centerCoordinates}
            zoom={DEFAULT_MAP_ZOOM}
            onViewportChange={handleViewportChange}
            onMapClick={handleMapClick}
            paths={pathsWithLegs}
            markers={markers}
            fitBoundsCoordinates={manualFit?.coords ?? fitBoundsCoordinates}
            fitBoundsNonce={manualFit?.nonce}
            fitBoundsPadding={
              manualFit ? RECENTER_FIT_PADDING_PX : undefined
            }
            apiKey={import.meta.env.VITE_MAP_API_KEY}
          />
        </MapWrapper>
      ) : (
        <NoMapMessage>
          <span>🗺️</span>
          <p>{mapError || t('surveillance.mapPlaceholder')}</p>
          {centerCoordinates && (
            <small>
              {t('surveillance.mapCenter')}: {centerCoordinates.lat.toFixed(4)},{' '}
              {centerCoordinates.lng.toFixed(4)}
            </small>
          )}
        </NoMapMessage>
      )}

      {/* Pantalla completa — tercer piso de la pila de la esquina, encima de
          recentrar. A diferencia de los otros dos NO se esconde con la
          leyenda desplegada: la leyenda crece hacia arriba desde la esquina y
          llega a tapar a recentrar, pero no a este, que va un piso más alto.
          Sí exige mapa real montado — sin mapa no hay nada que agrandar. */}
      {allowFullscreen && displayMap && centerCoordinates && (
        <FullscreenButton
          type="button"
          onClick={toggleFullscreen}
          aria-label={t(
            fullscreen
              ? 'surveillance.mapExitFullscreen'
              : 'surveillance.mapFullscreen'
          )}
          data-testid="map-fullscreen-toggle"
        >
          <LegendToggleIcon aria-hidden>
            {fullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
          </LegendToggleIcon>
        </FullscreenButton>
      )}

      {/* Botón de recentrar — misma pila de esquina que el toggle de
          leyenda, justo encima (ver RecenterButton en styles.ts). Solo con
          el mapa real montado (sin mapa no hay nada que encuadrar) y con
          la leyenda COLAPSADA: la LegendContainer expandida crece hacia
          arriba en la misma esquina y lo taparía. aria-label reutiliza
          `pages:surveillance.mapCenter` ("Centro del mapa"), key ya
          existente en este componente — ninguna key nueva. */}
      {displayMap && centerCoordinates && !hideLegend && !legendExpanded && (
        <RecenterButton
          type="button"
          onClick={handleRecenter}
          aria-label={t('surveillance.mapCenter')}
        >
          <LegendToggleIcon aria-hidden>
            <LocateIcon />
          </LegendToggleIcon>
        </RecenterButton>
      )}

      {/* Leyenda por estado de segmento — minimizada por defecto */}
      {!hideLegend &&
        (legendExpanded ? (
          <LegendContainer>
            <LegendHeader>
              <LegendCloseButton
                type="button"
                onClick={() => setLegendExpanded(false)}
                aria-label={t('common:expandInfo')}
              >
                <CloseIcon />
              </LegendCloseButton>
            </LegendHeader>
            <LegendItem>
              <LegendColor $color={theme.colors.routeStatus.completed} />
              <LegendLabel>
                {t('surveillance.segmentState.completed')} ({completedCount})
              </LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendColor $color={theme.colors.routeStatus.active} />
              <LegendLabel>
                {t('surveillance.segmentState.active')} ({activeCount})
              </LegendLabel>
            </LegendItem>
            {gpsPoints.length > 0 && (
              <LegendItem>
                <LegendColor $color={theme.colors.routeStatus.active} />
                <LegendLabel>
                  {t('surveillance.gps.tracking')}
                  {lastGpsSignalLabel ? ` · ${lastGpsSignalLabel}` : ''}
                </LegendLabel>
              </LegendItem>
            )}
            {/* Bloque "Pausas" — solo si esta ruta realmente tiene algo que
                mostrar (causas presentes y/o algún cambio de sentido). Cada
                fila de causa reutiliza `pages:surveillance.pause.causes.*`
                (misma key que el chip de la card del tramo); la fila de
                cambio de sentido reutiliza `modals:timerOverlay.changeDirection`
                — ninguna key nueva. Gateado por `directionChangeAtEndSegmentIds`
                (solo datos de segmento, no depende de que las coordenadas de
                `GET_POSITION_ROADS` ya hayan cargado) — así la leyenda
                refleja los datos de la ruta desde el primer render, sin
                parpadear vacía. Fusión 2026-07-09: la fila "Cambio de
                sentido" ahora muestra el dot-con-↺ (mismo swatch
                `LegendColor`, con `DirectionChangeIcon` centrado dentro vía
                `LegendDirectionChangeGlyph`) en vez de un punto negro liso —
                para que se lea como la MISMA familia visual que el marcador
                fusionado del mapa (ver sección de arriba). */}
            {(pauseCausesPresent.length > 0 ||
              directionChangeAtEndSegmentIds.size > 0) && (
              <>
                <LegendSectionTitle>
                  {t('surveillance.pause.pauses')}
                </LegendSectionTitle>
                {pauseCausesPresent.map((cause) => (
                  <LegendItem key={cause}>
                    <LegendColor $color={getPauseCauseColor(cause, theme)} />
                    <LegendLabel>
                      {t(`surveillance.pause.causes.${cause}`, cause)}
                    </LegendLabel>
                  </LegendItem>
                ))}
                {directionChangeAtEndSegmentIds.size > 0 && (
                  <LegendItem>
                    <LegendColor $color={theme.colors.black}>
                      <LegendDirectionChangeGlyph aria-hidden>
                        <DirectionChangeIcon />
                      </LegendDirectionChangeGlyph>
                    </LegendColor>
                    <LegendLabel>
                      {t('modals:timerOverlay.changeDirection')}
                    </LegendLabel>
                  </LegendItem>
                )}
              </>
            )}
          </LegendContainer>
        ) : (
          <LegendToggleButton
            type="button"
            onClick={() => setLegendExpanded(true)}
            aria-label={t('common:expandInfo')}
          >
            <LegendToggleIcon aria-hidden>
              <LayersIcon />
            </LegendToggleIcon>
          </LegendToggleButton>
        ))}
    </MapContainer>
  );
}
